import api from "@/services/api";
import type { CartCount, CartItem, CartService, CartSession } from "@/types";
import {
  getStoredCartSessionId,
  setStoredCartSessionId,
} from "@/utils/cart-session-storage";
import { readString, type UnknownRecord, unwrapData } from "@/utils/normalize";

const readSessionId = (payload: unknown) => {
  const data = unwrapData<UnknownRecord>(payload);
  return readString(data.session_id, data.sessionId) ?? "";
};

const buildSessionHeaders = (sessionId: string) => ({
  headers: {
    "x-session-id": sessionId,
  },
});

const mapCartPayload = (items: CartItem[]) =>
  items.map((item) => ({
    product_variant_id: item.productId,
    quantity: item.quantity,
  }));

export const apiCartProvider: CartService = {
  async ensureSession(): Promise<CartSession> {
    const existingSessionId = getStoredCartSessionId();

    if (existingSessionId) {
      return {
        sessionId: existingSessionId,
      };
    }

    const response = await api.get("/cart/session");
    const sessionId = readSessionId(response.data);

    if (!sessionId) {
      throw new Error("Không lấy được session giỏ hàng.");
    }

    setStoredCartSessionId(sessionId);

    return {
      sessionId,
    };
  },

  async syncGuestCart(items: CartItem[]) {
    const { sessionId } = await this.ensureSession();
    await api.delete("/cart/clear", buildSessionHeaders(sessionId));

    if (items.length === 0) {
      return;
    }

    await api.post(
      "/cart/add-many",
      mapCartPayload(items),
      buildSessionHeaders(sessionId),
    );
  },

  async mergeCart() {
    const { sessionId } = await this.ensureSession();
    await api.post("/cart/merge", undefined, buildSessionHeaders(sessionId));
  },

  async fetchCartCount(): Promise<CartCount> {
    const { sessionId } = await this.ensureSession();
    const response = await api.get(
      "/cart/count",
      buildSessionHeaders(sessionId),
    );
    const data = unwrapData<UnknownRecord>(response.data);
    const count = data.count;

    return {
      count: typeof count === "number" ? count : 0,
    };
  },
};
